import React, { useState, useEffect } from 'react';
import BigBlockDisplay from '../components/BigBlockDisplay.jsx';
import EnhancedStatCard from '../components/EnhancedStatCard.jsx';
import NetworkStatsCard from '../components/NetworkStatsCard.jsx';
import HeartbeatLine from '../components/HeartbeatLine.jsx';
import TickTock from '../components/TickTock.jsx';
import GlowText from '../components/GlowText.jsx';
import { fetchBlockHeight, fetchLatestBlock, fetchBitcoinPrice, fetchFees, fetchNetworkStats } from '../api/bitcoinService';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import SafeIcon from '../components/common/SafeIcon';

const Home = () => {
  const [blockHeight, setBlockHeight] = useState(null);
  const [lastBlockTime, setLastBlockTime] = useState(null);
  const [priceData, setPriceData] = useState(null);
  const [fees, setFees] = useState(null);
  const [networkStats, setNetworkStats] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [timeSinceBlock, setTimeSinceBlock] = useState({ minutes: 0, seconds: 0 });
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);

  // Fetch only block height (most frequent update needed)
  const updateBlockHeight = async () => {
    try {
      const height = await fetchBlockHeight();
      if (height) {
        setBlockHeight(height);
        console.log('Block height updated:', height);
      }
    } catch (error) {
      console.error("Error updating block height:", error);
    }
  };

  // Fetch network stats only (hashrate and difficulty)
  const updateNetworkStats = async () => {
    try {
      const stats = await fetchNetworkStats();
      if (stats) {
        setNetworkStats(stats);
        console.log('Network stats updated:', stats);
      }
    } catch (error) {
      console.error("Error updating network stats:", error);
    }
  };

  // Prices only (less frequent than fees)
  const updatePrices = async () => {
    try {
      const currentPriceData = await fetchBitcoinPrice();
      if (currentPriceData) {
        setPriceData(currentPriceData);
        console.log('Price data updated:', currentPriceData);
      }
    } catch (error) {
      console.error("Error updating prices:", error);
    }
  };

  // Fees only (most frequent of market data)
  const updateFees = async () => {
    try {
      const currentFees = await fetchFees();
      if (currentFees) {
        setFees(currentFees);
        console.log('Fees updated:', currentFees);
      }
    } catch (error) {
      console.error("Error updating fees:", error);
    }
  };

  // Initial data load
  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Fetch all data in parallel for initial load
      const [
        height, 
        latestBlock, 
        currentPriceData, 
        currentFees, 
        currentNetworkStats
      ] = await Promise.all([
        fetchBlockHeight(),
        fetchLatestBlock(),
        fetchBitcoinPrice(),
        fetchFees(),
        fetchNetworkStats()
      ]);

      console.log('Initial data loaded:', {
        height,
        latestBlock,
        currentPriceData,
        currentFees,
        currentNetworkStats
      });

      if (height) setBlockHeight(height);
      if (latestBlock?.timestamp) setLastBlockTime(new Date(latestBlock.timestamp * 1000));
      if (currentPriceData) setPriceData(currentPriceData);
      if (currentFees) setFees(currentFees);
      if (currentNetworkStats) setNetworkStats(currentNetworkStats);
      setLastUpdate(new Date());
      
    } catch (error) {
      console.error("Error loading initial data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Update time since last block
  useEffect(() => {
    const interval = setInterval(() => {
      if (lastBlockTime) {
        const now = new Date();
        const diff = Math.floor((now - lastBlockTime) / 1000);
        setTimeSinceBlock({
          minutes: Math.floor(diff / 60),
          seconds: diff % 60
        });
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [lastBlockTime]);

  // Update current date
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDate(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Load initial data
    loadInitialData();
    
    // Optimized API Call Intervals:
    // 1. Block Height: Every 15 seconds (critical for block detection)
    // 2. Network Stats (Hashrate/Difficulty): Every 60 minutes (very slow changing)
    // 3. Prices: Every 10 minutes (moderate frequency)
    // 4. Fees: Every 1 minute (high frequency for user experience)
    
    const blockInterval = setInterval(updateBlockHeight, 15000); // Every 15 seconds for block height
    const networkInterval = setInterval(updateNetworkStats, 3600000); // Every 60 minutes (3,600,000 ms) for network stats
    const priceInterval = setInterval(updatePrices, 600000); // Every 10 minutes (600,000 ms) for prices
    const feeInterval = setInterval(updateFees, 60000); // Every 1 minute (60,000 ms) for fees
    
    return () => {
      clearInterval(blockInterval);
      clearInterval(networkInterval);
      clearInterval(priceInterval);
      clearInterval(feeInterval);
    };
  }, []);

  const formatCurrency = (val, currency) => {
    if (!val || isNaN(val)) return '...';
    // Use the already rounded value from the API
    return new Intl.NumberFormat('de-DE', { 
      style: 'currency', 
      currency: currency,
      minimumFractionDigits: 0, // No decimal places
      maximumFractionDigits: 0  // No decimal places
    }).format(val);
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  const formatTime = (date) => {
    return new Intl.DateTimeFormat('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    }).format(date);
  };

  // Calculate time since last update for each data type
  const getTimeSinceUpdate = (lastUpdateTime) => {
    const now = new Date();
    const diffSeconds = Math.floor((now - lastUpdateTime) / 1000);
    if (diffSeconds < 60) return `${diffSeconds} Sekunden`;
    if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)} Minuten`;
    return `${Math.floor(diffSeconds / 3600)} Stunden`;
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden flex flex-col">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-[500px] bg-gradient-to-b from-bitcoin/5 to-transparent pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-bitcoin/5 rounded-full blur-[120px] pointer-events-none" />
      
      {/* Heartbeat Animation */}
      <HeartbeatLine />

      {/* Header */}
      <header className="p-6 flex justify-between items-center relative z-20 border-b border-white/5">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-bitcoin to-bitcoin-dark flex items-center justify-center font-bold text-black border border-white/20">
            ₿
          </div>
          <span className="font-bold text-xl tracking-tight">Bitcoin<span className="text-bitcoin">Uhr</span></span>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="text-xs text-gray-400 font-mono">
            {formatDate(currentDate)}
          </div>
          <div className="text-xs text-gray-500 font-mono hidden sm:block">
            NETZWERK STATUS: <span className="text-green-500">ONLINE</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow flex flex-col items-center justify-center p-4 relative z-10 w-full max-w-7xl mx-auto">
        <div className="w-full py-12 md:py-20">
          <BigBlockDisplay height={blockHeight} />
          
          {/* Time since last block */}
          {timeSinceBlock.minutes > 0 && (
            <div className="text-center mt-4 text-gray-400">
              <span className="text-sm">
                {timeSinceBlock.minutes} Minuten {timeSinceBlock.seconds} Sekunden seit dem letzten Block
              </span>
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full max-w-4xl mt-8">
          {/* Bitcoin Price */}
          <EnhancedStatCard 
            title="Bitcoin Wert" 
            icon={FaIcons.FaBitcoin}
            items={[
              { label: "1 BTC", value: "1 BTC" },
              { label: "EUR", value: formatCurrency(priceData?.EUR?.rate_float, 'EUR') },
              { label: "USD", value: formatCurrency(priceData?.USD?.rate_float, 'USD') }
            ]}
          />

          {/* Transaction Fees */}
          <EnhancedStatCard 
            title="Transaktationsgebühren" 
            icon={FiIcons.FiZap}
            items={[
              { label: "Hohe Priorität", value: `${fees?.fastestFee || '-'} sat/vB`, subValue: "~10 min" },
              { label: "Mittlere Priorität", value: `${fees?.halfHourFee || '-'} sat/vB`, subValue: "~30 min" },
              { label: "Niedrige Priorität", value: `${fees?.hourFee || '-'} sat/vB`, subValue: "~1 Std." }
            ]}
          />

          {/* Network Stats */}
          <NetworkStatsCard 
            hashrate={{ value: networkStats?.current_hashrate, trend: 'up' }}
            difficulty={{ value: networkStats?.current_difficulty, trend: 'up' }}
          />
        </div>

        {/* Last Update Time */}
        <div className="mt-8 text-center text-xs text-gray-500 font-mono">
          Letzte Aktualisierung: {formatTime(lastUpdate)}
          {loading && <span className="ml-2 text-bitcoin">(Laden...)</span>}
        </div>

        {/* API Status Display */}
        <div className="mt-4 text-center text-xs text-gray-600 font-mono space-y-1">
          <div>✅ Block-Updates: Alle 15 Sekunden</div>
          <div>📈 Netzwerk-Stats: Alle 60 Minuten</div>
          <div>💰 Preise: Alle 10 Minuten</div>
          <div>⚡ Gebühren: Alle 1 Minute</div>
          <div>🕐 Zeit-Display: Jede Sekunde</div>
        </div>

        {/* Debug Info - Remove in production */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-4 text-center text-xs text-gray-600 font-mono">
            <div>Price EUR rate_float (rounded): {priceData?.EUR?.rate_float}</div>
            <div>Price USD rate_float (rounded): {priceData?.USD?.rate_float}</div>
            <div>Hashrate (from blockchain.com): {networkStats?.current_hashrate}</div>
            <div>Difficulty (from blockchain.com): {networkStats?.current_difficulty}</div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="p-6 text-center text-gray-600 text-sm relative z-20 border-t border-white/5">
        <p>Daten bereitgestellt von mempool.space & blockchain.info</p>
      </footer>

      {/* Tick Tock */}
      <TickTock />
    </div>
  );
};

export default Home;